import { SpintrTypes } from "src/typings";
import { UiActionTypes as ActionTypes, UiAction } from "./action-types";

export interface IResponsiveState {
    menuVisible: boolean;
    profileVisible: boolean;
    searchVisible: boolean;
    submenuVisible: boolean;
    shortcutsVisible: boolean;
}

export interface IReactionPickerConfig {
    isVisible: boolean;
    elementId: string;
    uberId: number;
    yOffset: number;
    xOffset: number;
    identity?: any;
    sendToMixpanel: boolean;
    isChat?: boolean;
}

export interface IUiState {
    /**
     * Whether the user is idle or not.
     */
    userIsIdle: boolean;

    /**
     * The last activity timestamp of the user.
     */
    lastUserActivity: number;

    /**
     * Whether the screen is locked or not.
     */
    screenLocked: boolean;

    /**
     * The current color mode of the application.
     */
    colorMode: "light" | "dark" | "system" | "contrast-light" | "contrast-dark";

    activeActionMenu: string;
    activeSidebarTab: number;
    displayAsOfflineInChat: boolean;
    isTinyInModal: boolean;
    isSmallViewMode: boolean;
    isWebViewMode: boolean;
    responsive: IResponsiveState;
    sidebarExpanded: boolean;
    formFooterBarVisible: boolean;
    formFooterBarTop: boolean;
    shouldReloadFavourites: boolean;
    currentlyShowingTeaserboxes: number[];
    viewMode: Spintr.ViewMode;
    startViewKey: number;
    focusCommentFieldForUberId: number;
    isInTeamsApp: boolean;
    appMode: boolean;
    visage2FullscreenMode: boolean;
    visage2FullscreenHeaderVisible: boolean;
    globalBreadcrumbs?: Spintr.IBreadcrumbItem[];
    uploadsInProgress: any[];
    public360: any;
    overlayId: string;
    displayHeaderSearch: boolean;
    headerSearchBarMode: SpintrTypes.HeaderSearchBarMode;
    useDocumentWidthMode: boolean;
    useNarrowFormWidthMode: boolean;
    disableWrapperBackground: boolean;
    reactionPickerConfig?: IReactionPickerConfig;
    startPageBuilderMode: SpintrTypes.StartPageBuilderMode;
    displayGreyBackground: boolean;
    adminNoPadding: boolean;
}

const initialState: IUiState = {
    colorMode: "system",
    userIsIdle: false,
    screenLocked: false,
    lastUserActivity: Date.now(),
    headerSearchBarMode: SpintrTypes.HeaderSearchBarMode.Search,
    activeActionMenu: "",
    activeSidebarTab: 0,
    currentlyShowingTeaserboxes: [],
    displayAsOfflineInChat: false,
    isSmallViewMode: false,
    isWebViewMode: false,
    isTinyInModal: false,
    responsive: {
        menuVisible: false,
        profileVisible: false,
        searchVisible: false,
        submenuVisible: false,
        shortcutsVisible: false
    },
    formFooterBarVisible: false,
    formFooterBarTop: false,
    shouldReloadFavourites: false,
    sidebarExpanded: true,
    viewMode: 0,
    startViewKey: 0,
    focusCommentFieldForUberId: undefined,
    isInTeamsApp: window.location.href.indexOf("isInTeamsApp=true") > -1,
    appMode: window.location.href.indexOf("isInTeamsApp=true") > -1,
    visage2FullscreenMode: false,
    visage2FullscreenHeaderVisible: false,
    globalBreadcrumbs: [],
    uploadsInProgress: [],
    public360: {},
    overlayId: "",
    displayHeaderSearch: false,
    useDocumentWidthMode: false,
    useNarrowFormWidthMode: false,
    disableWrapperBackground: false,
    reactionPickerConfig: undefined,
    startPageBuilderMode: SpintrTypes.StartPageBuilderMode.Edit,
    displayGreyBackground: false,
    adminNoPadding: false,
};

const reducer = (state: IUiState = initialState, action: UiAction) => {
    switch (action.type) {
        case ActionTypes.SetUserIdle:
            return {
                ...state,
                userIsIdle: action.isIdle
            }

        case ActionTypes.SetUserActivity:
            return {
                ...state,
                lastUserActivity: action.lastActivity
            };

        case ActionTypes.SetScreenLocked:
            return {
                ...state,
                screenLocked: action.isLocked
            };

        case ActionTypes.SetHeaderSearchBarMode:
            return {
                ...state,
                headerSearchBarMode: action.mode
            };

        case ActionTypes.SetSidebarExpanded:
            return {
                ...state,
                sidebarExpanded: action.isExpanded
            };

        case ActionTypes.SetResponsiveMenuVisible:
            return {
                ...state,
                responsive: {
                    ...state.responsive,
                    menuVisible: action.visible,
                    profileVisible: false,
                    searchVisible: false,
                    submenuVisible: false,
                    shortcutsVisible: false
                },
            };

        case ActionTypes.SetResponsiveProfileVisible:
            return {
                ...state,
                responsive: {
                    ...state.responsive,
                    menuVisible: false,
                    profileVisible: action.visible,
                    searchVisible: false,
                    submenuVisible: false,
                    shortcutsVisible: false
                },
            };

        case ActionTypes.SetResponsiveShortcutsVisible:
            return {
                ...state,
                responsive: {
                    ...state.responsive,
                    menuVisible: false,
                    profileVisible: false,
                    searchVisible: false,
                    submenuVisible: false,
                    shortcutsVisible: action.visible
                },
            };

        case ActionTypes.SetResponsiveSearchVisible:
            return {
                ...state,
                responsive: {
                    ...state.responsive,
                    menuVisible: false,
                    profileVisible: false,
                    searchVisible: action.visible,
                    submenuVisible: false,
                    shortcutsVisible: false,
                },
            };

        case ActionTypes.SetResponsiveSubmenuVisible:
            return {
                ...state,
                responsive: {
                    ...state.responsive,
                    menuVisible: false,
                    profileVisible: false,
                    searchVisible: false,
                    submenuVisible: action.visible,
                    shortcutsVisible: false
                },
            };

        case ActionTypes.SetViewMode:
            return {
                ...state,
                viewMode: action.viewMode,
                isSmallViewMode: action.viewMode <= 1021,
                isWebViewMode: action.viewMode <= 425,
            };

        case ActionTypes.SetIsTinyInModal:
            return {
                ...state,
                isTinyInModal: action.isTinyInModal,
            };

        case ActionTypes.SET_ACTIVE_ACTION_MENU:
            return {
                ...state,
                activeActionMenu: action.activeActionMenu,
            };
            
        case ActionTypes.SetDisplayAsOfflineInChat:
            return {
                ...state,
                displayAsOfflineInChat: action.displayAsOfflineInChat,
            };

        case ActionTypes.SetActiveSidebarTab:
            return {
                ...state,
                activeSidebarTab: action.activeSidebarTab,
            };

        case ActionTypes.SetFormFooterBarVisible:
            return {
                ...state,
                formFooterBarVisible: action.visible,
            };
            
        case ActionTypes.SetFormFooterBarTop:
            return {
                ...state,
                formFooterBarTop: action.top,
            };

        case ActionTypes.SetShouldReloadFavourites:
            return {
                ...state,
                shouldReloadFavourites: action.shouldReload,
            };

        case ActionTypes.setCurrentlyShowingTeaserboxes:
            return {
                ...state,
                currentlyShowingTeaserboxes: action.teaserboxes,
            };

        case ActionTypes.IncrementStartViewKey:
            return {
                ...state,
                startViewKey: state.startViewKey ? (state.startViewKey + 1) : 1,
            };

        case ActionTypes.SetFocusCommentFieldForUberId:
            return {
                ...state,
                focusCommentFieldForUberId: action.focusCommentFieldForUberId,
            };

        case ActionTypes.SetIsInTeamsApp:
            return {
                ...state,
                isInTeamsApp: action.isInTeamsApp,
            };
            
        case ActionTypes.SetAppMode:
            return {
                ...state,
                appMode: action.appMode,
            };

        case ActionTypes.SetVisage2FullscreenMode:
            return {
                ...state,
                visage2FullscreenMode: action.visage2FullscreenMode,
            };

        case ActionTypes.SetVisage2FullscreenHeaderVisible:
            return {
                ...state,
                visage2FullscreenHeaderVisible: action.visage2FullscreenHeaderVisible,
            };

        case ActionTypes.SetGlobalBreadcrumbs:
            return {
                ...state,
                globalBreadcrumbs: action.value,
            };

        case ActionTypes.AddUploads:
            return {
                ...state,
                uploadsInProgress: !state.uploadsInProgress
                    ? action.uploads
                    : [...state.uploadsInProgress, ...action.uploads],
            };

        case ActionTypes.ClearUploads:
            return {
                ...state,
                uploadsInProgress: [],
            };
            
        case ActionTypes.UpdateUpload:
            return {
                ...state,
                uploadsInProgress: state.uploadsInProgress.map(uip => {
                    return uip.uploadId === action.upload.uploadId ?
                        action.upload :
                        uip
                }),
            };

        case ActionTypes.RemoveUpload:
            return {
                ...state,
                uploadsInProgress: state.uploadsInProgress.filter(u => u.uploadId !== action.uploadId),
            };

        case ActionTypes.SetPublic360SelectedUnitLevel1:
            return {
                ...state,
                public360: {
                    ...state.public360,
                    selectedUnitLevel1: action.unit,
                }
            };

        case ActionTypes.SetPublic360SelectedUnitLevel2:
            return {
                ...state,
                public360: {
                    ...state.public360,
                    selectedUnitLevel2: action.unit,
                },
            };

        case ActionTypes.SetOverlayId:
            return {
                ...state,
                overlayId: action.value,
            };

        case ActionTypes.SetDisplayHeaderSearch:
            return {
                ...state,
                displayHeaderSearch: action.value,
            };

        case ActionTypes.SetUseDocumentWidthMode:
            return {
                ...state,
                useDocumentWidthMode: action.value
            };

        case ActionTypes.SetUseNarrowFormWidthMode:
            return {
                ...state,
                useNarrowFormWidthMode: action.value,
            };
            
        case ActionTypes.SetDisableWrapperBackground:
            return {
                ...state,
                disableWrapperBackground: action.value,
            };

        case ActionTypes.SetReactionPickerConfig:
            return {
                ...state,
                reactionPickerConfig: action.value,
            };
            
        case ActionTypes.SetStartPageBuilderMode:
            return {
                ...state,
                startPageBuilderMode: action.value,
            };

        case ActionTypes.ToggleGreyBackground:
            return {
                ...state,
                displayGreyBackground: action.displayGreyBackground,
            };

        case ActionTypes.SetAdminNoPadding:
            return {
                ...state,
                adminNoPadding: action.value,
            };

        case ActionTypes.SetColorMode:
            return {
                ...state,
                colorMode: action.colorMode,
            };

        default:
            return state;
    }    
};

export default reducer;
